import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/use-auth";
import { Eye, Loader2 } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

import { getConfigMultipliers } from "@/pages/admin/dashboard/api/get-config-multipliers";
import { useQuery } from "@tanstack/react-query";
import { getAkumulasiIku } from "../api/get-akumulasi-iku";
import { getIku1 } from "../api/get-iku-1";
import { getIku2 } from "../api/get-iku-2";
import { getIku3 } from "../api/get-iku-3";
import {
  getIku4,
  getIku5,
  getIku6,
  getIku7,
  getIku8,
} from "../api/get-iku-penelitian";

const formatPoint = (point: number | string | undefined | null) => {
  if (point === null || point === undefined) return "0";
  const num = typeof point === "string" ? parseFloat(point) : point;
  if (isNaN(num)) return "0";
  return num.toFixed(2);
};

import {
  POINT_MAX_IKU,
  calculateIKU4Koresponden,
  calculateIKU4NonKoresponden,
  calculateIKU5Koresponden,
  calculateIKU5NonKoresponden,
  calculateIKU7Koresponden,
  calculateIKU7NonKoresponden,
  getDynamicIkuList,
  isInRENRange,
} from "../utils/iku-calculations";

interface TabelIkuProps {
  jabatanFungsional?: string;
  activeIku?: string; // e.g. "IKU 6"
  isClaiming?: boolean;
  isClaimed?: boolean;
  onClaimIku?: (iku: string | null) => void;
  onDropIku?: (iku: string) => void;
  niu?: string;
  year?: string | number;
  semester?: string | number;
  periodeId?: string | number | null;
  month?: string | null;
  activeProduct?: any;
  onTotalPointDiperolehChange?: (val: number) => void;
  showNilai?: boolean;
  unitHomebase?: { prodi?: string; jurusan?: string; fakultas?: string };
}

export function TabelIku({
  jabatanFungsional = "",
  activeIku,
  isClaiming,
  isClaimed,
  onClaimIku,
  onDropIku,
  niu,
  year,
  semester,
  periodeId,
  month,
  activeProduct,
  onTotalPointDiperolehChange,
  showNilai = true,
  unitHomebase,
}: TabelIkuProps) {
  const { isAuthenticated } = useAuth();
  const [dragOverIku, setDragOverIku] = useState<string | null>(null);

  const getColumnKey = (jabatan: string) => {
    const j = jabatan.toLowerCase();
    if (j.includes("guru besar")) return "gb";
    if (j.includes("lektor kepala")) return "lk";
    if (j.includes("lektor")) return "l";
    if (j.includes("asisten ahli")) return "aa";
    return "tp";
  };

  const colKey = getColumnKey(jabatanFungsional);

  const columnLabel = {
    tp: "TP",
    aa: "AA",
    l: "L",
    lk: "LK",
    gb: "GB",
  }[colKey];

  const { data: configMultipliers } = useQuery({
    queryKey: ["config-multipliers"],
    queryFn: getConfigMultipliers,
    enabled: isAuthenticated,
  });

  const finalIkuList = useMemo(() => {
    return getDynamicIkuList(configMultipliers);
  }, [configMultipliers]);

  const { data: ikuResponse } = useQuery({
    queryKey: ["iku-6-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku6(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku4Response } = useQuery({
    queryKey: ["iku-4-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku4(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku5Response } = useQuery({
    queryKey: ["iku-5-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku5(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku7Response } = useQuery({
    queryKey: ["iku-7-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku7(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku8Response } = useQuery({
    queryKey: ["iku-8-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku8(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku3Response } = useQuery({
    queryKey: ["iku-3-penelitian", niu, year, semester, periodeId, month],
    queryFn: () => getIku3(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku1Response } = useQuery({
    queryKey: ["iku-1-pendidikan", niu, year, semester, periodeId, month],
    queryFn: () => getIku1(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const { data: iku2Response } = useQuery({
    queryKey: ["iku-2-pendidikan", niu, year, semester, periodeId, month],
    queryFn: () => getIku2(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const ikuList = [
    ...(iku1Response?.iku_list || []),
    ...(iku2Response?.iku_list || []),
    ...(iku3Response?.iku_list || []),
    ...(ikuResponse?.iku_list || []),
    ...(iku4Response?.iku_list || []),
    ...(iku5Response?.iku_list || []),
    ...(iku7Response?.iku_list || []),
    ...(iku8Response?.iku_list || []),
  ];

  const { data: akumulasiIkuResponse } = useQuery({
    queryKey: ["akumulasi-iku", niu, year, semester, periodeId, month],
    queryFn: () => getAkumulasiIku(niu!, year!, semester!, periodeId!, month),
    enabled:
      !!niu &&
      !!year &&
      !!semester &&
      !!periodeId &&
      !!month &&
      isAuthenticated,
  });

  const akumulasiData = akumulasiIkuResponse?.datas;
  const rawTotalPointDiperoleh = akumulasiData?.total_point || 0;

  const globalMaxPoint =
    POINT_MAX_IKU[columnLabel as keyof typeof POINT_MAX_IKU] || 0;
  const iku6RealPoint =
    Number(ikuList.find((iku: any) => iku.iku_code === "IKU 6")?.real_point) ||
    0;

  const allowedMaxPoint = globalMaxPoint + iku6RealPoint;
  const finalTotalPoint = Math.min(rawTotalPointDiperoleh, allowedMaxPoint);

  useEffect(() => {
    if (onTotalPointDiperolehChange) {
      onTotalPointDiperolehChange(finalTotalPoint);
    }
  }, [finalTotalPoint, onTotalPointDiperolehChange]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-3 py-1.5 bg-white border border-slate-200 rounded-lg shadow-sm shrink-0">
        <div className="flex flex-col">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            Total Point Diperoleh
          </span>
        </div>
        <div className="flex items-center gap-3">
          {globalMaxPoint > 0 && (
            <span className="text-[9px] font-semibold text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200/60 shadow-sm">
              Max Point ({columnLabel}):{" "}
              {showNilai ? formatPoint(globalMaxPoint) : "***"}
              {/* {iku6RealPoint > 0 && (
                <span className="text-indigo-600">
                  {" "}
                  + IKU 6 ({showNilai ? formatPoint(iku6RealPoint) : "***"})
                </span>
              )} */}
            </span>
          )}
          <div className="bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-md flex items-center gap-2 shadow-inner">
            {rawTotalPointDiperoleh > finalTotalPoint && (
              <span className="text-sm font-bold text-slate-400 line-through decoration-slate-400/60 decoration-2">
                {showNilai ? formatPoint(rawTotalPointDiperoleh) : "***"}
              </span>
            )}
            <span className="text-xl font-black text-indigo-600 leading-none">
              {showNilai ? formatPoint(finalTotalPoint) : "***"}
            </span>
          </div>
        </div>
      </div>

      {/* {activeProduct && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50/50 border border-indigo-100 rounded-lg shadow-sm shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">
              Karya Aktif Terpilih
            </span>
            <span className="text-xs font-semibold text-slate-700 line-clamp-1">
              {activeProduct.sks_judul ||
                activeProduct.details_parsed?.judul_karya ||
                "Tanpa Judul"}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {activeProduct.details_parsed?.is_ketua !== undefined && (
              <Badge
                variant="outline"
                className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${
                  activeProduct.details_parsed.is_ketua
                    ? "bg-indigo-100 text-indigo-700 border-indigo-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {activeProduct.details_parsed.is_ketua ? "Ketua" : "Anggota"}
              </Badge>
            )}
            {activeProduct.details_parsed?.jumlah_total_anggota && (
              <Badge
                variant="outline"
                className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-white text-slate-600 border-slate-200"
              >
                {activeProduct.details_parsed.jumlah_total_anggota} Anggota
              </Badge>
            )}
          </div>
        </div>
      )} */}

      <div className="overflow-visible bg-white">
        <Table>
          <TableHeader className="bg-slate-50/80 backdrop-blur-md sticky top-0 z-20 shadow-sm shadow-slate-200/50 outline outline-slate-200/50">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16 text-center text-slate-700 font-bold border-r border-slate-200 uppercase tracking-wider text-[10px] py-1 h-8">
                IKU
              </TableHead>
              <TableHead className="min-w-64 text-slate-700 font-bold border-r border-slate-200 uppercase tracking-wider text-[10px] py-1 h-8">
                INDIKATOR
              </TableHead>
              {/* <TableHead className="text-center text-slate-700 font-bold border-r border-slate-200 uppercase tracking-wider text-[10px] w-24 py-1 h-8">
                Persentase
              </TableHead> */}

              <TableHead className="text-center text-slate-700 font-bold border-r border-slate-200 uppercase tracking-wider text-[10px] w-32 py-1 h-8">
                Max Point
              </TableHead>
              <TableHead className="text-center text-slate-700 font-bold uppercase tracking-wider text-[10px] w-32 py-1 h-8">
                Point Diperoleh
              </TableHead>
              <TableHead className="w-10 py-1 h-8"></TableHead>
              {onClaimIku !== undefined && (
                <TableHead className="w-24 text-center text-slate-700 font-bold uppercase tracking-wider text-[10px] py-1 h-8">
                  Aksi
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const activeIkuArray = activeIku
                ? activeIku
                    .toUpperCase()
                    .split(",")
                    .map((s: string) => s.trim())
                : null;

              let maxPotentialIku: number | null = null;
              let maxPointValue = -1;

              if (activeProduct?.details_parsed) {
                const getPotentialPoint = (ikuNum: number) => {
                  const isAllowed =
                    !activeProduct.iku ||
                    activeProduct.iku.toUpperCase().includes(`IKU ${ikuNum}`);
                  if (!isAllowed) return -1;

                  const isKetua =
                    activeProduct.details_parsed.is_ketua === true;
                  const isKoresponden =
                    activeProduct.details_parsed.is_koresponden === true;
                  const jumlahAnggota =
                    Number(activeProduct.details_parsed.jumlah_total_anggota) ||
                    1;
                  const pointSKS = activeProduct.point_sks || 0;
                  const jumlah = activeProduct.jumlah || 1;
                  const renCode = activeProduct.kode
                    ? activeProduct.kode.replace("ren_", "REN").toUpperCase()
                    : "";

                  let persentase = 0;
                  if (ikuNum === 5) {
                    if (isInRENRange(renCode, 250, 279)) {
                      persentase = calculateIKU5Koresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                        isKoresponden,
                      );
                    } else {
                      persentase = calculateIKU5NonKoresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                      );
                    }
                  } else if (ikuNum === 4) {
                    if (isInRENRange(renCode, 250, 279)) {
                      persentase = calculateIKU4Koresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                        isKoresponden,
                      );
                    } else {
                      persentase = calculateIKU4NonKoresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                      );
                    }
                  } else if (ikuNum === 7) {
                    if (isInRENRange(renCode, 250, 279)) {
                      persentase = calculateIKU7Koresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                        isKoresponden,
                      );
                    } else {
                      persentase = calculateIKU7NonKoresponden(
                        pointSKS,
                        jumlah,
                        jumlahAnggota,
                        isKetua,
                      );
                    }
                  } else {
                    persentase = 1.0;
                  }

                  const maxPoint =
                    POINT_MAX_IKU[columnLabel as keyof typeof POINT_MAX_IKU] ||
                    0;
                  const targetRow = finalIkuList.find((r) => r.iku === ikuNum);
                  const persentaseIkuList =
                    targetRow?.persentase?.[
                      colKey as keyof typeof targetRow.persentase
                    ] || 0;

                  if (ikuNum === 4 || ikuNum === 5 || ikuNum === 7) {
                    return (maxPoint * persentaseIkuList * persentase) / 10;
                  }

                  return maxPoint * persentaseIkuList * persentase;
                };

                const pts = [4, 5, 7].map((num) => ({
                  iku: num,
                  point: getPotentialPoint(num),
                }));

                for (const p of pts) {
                  if (p.point > maxPointValue && p.point >= 0) {
                    maxPointValue = p.point;
                    maxPotentialIku = p.iku;
                  }
                }

                if (activeIkuArray && activeIkuArray.length <= 1) {
                  maxPotentialIku = null;
                }
              }

              return finalIkuList.map((row: any, i) => {
                const rowIkuText = `IKU ${row.iku}`.toUpperCase();
                const isActive = activeIkuArray
                  ? activeIkuArray.includes(rowIkuText)
                  : true;
                const cellDisabledClass = isActive
                  ? ""
                  : "opacity-40 grayscale pointer-events-none";

                const matchedIkuData = ikuList.find(
                  (iku: any) => iku.iku_code === rowIkuText,
                );

                const akumulasiMatched = akumulasiData?.iku_list?.find(
                  (iku: any) => iku.kode_iku === rowIkuText,
                );

                return (
                  <Fragment key={i}>
                    <TableRow
                      className={`hover:bg-slate-50/50 transition-all duration-300 ${
                        dragOverIku === row.iku
                          ? "bg-indigo-50/80 outline-dashed outline-2 outline-indigo-400 -outline-offset-2"
                          : maxPotentialIku === row.iku && activeProduct
                            ? "bg-emerald-50/60 outline outline-emerald-400 -outline-offset-2 shadow-[inset_0_0_20px_rgba(52,211,153,0.15)] relative z-10"
                            : ""
                      }`}
                      onDragOver={(e) => {
                        const isIku7NotKetua = false; // row.iku === 7 && activeProduct?.details_parsed?.is_ketua === false;
                        if (isActive && onDropIku && !isIku7NotKetua) {
                          e.preventDefault();
                          setDragOverIku(row.iku);
                        }
                      }}
                      onDragLeave={() => {
                        if (isActive && onDropIku) {
                          setDragOverIku(null);
                        }
                      }}
                      onDrop={(e) => {
                        const isIku7NotKetua = false; // row.iku === 7 && activeProduct?.details_parsed?.is_ketua === false;
                        if (isActive && onDropIku && !isIku7NotKetua) {
                          e.preventDefault();
                          setDragOverIku(null);

                          if (
                            [4, 5, 7].includes(row.iku) &&
                            activeProduct?.details_parsed
                          ) {
                            // const isKetua =
                            //   activeProduct.details_parsed.is_ketua === true;
                            // const jumlahAnggota =
                            //   Number(
                            //     activeProduct.details_parsed
                            //       .jumlah_total_anggota,
                            //   ) || 1;
                            // // const totalKaryaKetua = 1;
                            //
                            // let persentase = 0;
                            // if (row.iku === 5) {
                            //   persentase = calculateIKU5(
                            //     isKetua,
                            //     jumlahAnggota,
                            //   );
                            // } else if (row.iku === 4) {
                            //   persentase = calculateIKU4(
                            //     isKetua,
                            //     jumlahAnggota,
                            //     columnLabel || "",
                            //   );
                            // } else if (row.iku === 7) {
                            //   const isKoresponden =
                            //     activeProduct.details_parsed.is_koresponden ===
                            //     true;
                            //   persentase = calculateIKU7(
                            //     isKetua,
                            //     isKoresponden,
                            //     jumlahAnggota,
                            //   );
                            // }
                            //
                            // const maxPoint =
                            //   POINT_MAX_IKU[
                            //     columnLabel as keyof typeof POINT_MAX_IKU
                            //   ] || 0;
                            // const persentaseIkuList =
                            //   row.persentase?.[
                            //     colKey as keyof typeof row.persentase
                            //   ] || 0;
                            // const finalPoint =
                            //   maxPoint * persentaseIkuList * persentase;
                            // console.log(`Perolehan Point IKU ${row.iku}:`, {
                            //   isKetua,
                            //   jumlahAnggota,
                            //   totalKaryaKetua:
                            //     row.iku === 7 ? totalKaryaKetua : undefined,
                            //   kodeJabatan: columnLabel,
                            //   persentaseFungsi: persentase,
                            //   maxPoint,
                            //   persentaseIkuList,
                            //   finalPoint,
                            // });
                          }

                          onDropIku(`IKU ${row.iku}`);
                        }
                      }}
                    >
                      <TableCell
                        className={`text-center font-medium border-r border-slate-200 py-1.5 ${isActive ? "text-indigo-600 font-bold" : "text-slate-800"} ${cellDisabledClass}`}
                      >
                        {row.iku}
                      </TableCell>
                      <TableCell
                        className={`font-medium border-r border-slate-200 py-1.5 ${isActive ? "text-indigo-700 font-bold" : "text-slate-700"} ${cellDisabledClass}`}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs leading-tight">
                              {row.indikator}
                            </span>
                            {maxPotentialIku === row.iku && activeProduct && (
                              <Badge className="bg-emerald-500 text-white border-transparent shadow-sm text-[9px] px-1.5 py-0.5 whitespace-nowrap animate-pulse flex items-center gap-1">
                                <span className="relative flex h-1.5 w-1.5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-200 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-100"></span>
                                </span>
                                Rekomendasi
                              </Badge>
                            )}
                          </div>
                          {/* {row.iku === 7 && (
                            <span className="text-[9px] text-amber-700 font-medium leading-tight bg-amber-50 px-2 py-1 rounded border border-amber-200/60 w-fit">
                              *Karya yang diakui nilainya adalah sebagai ketua
                              saja.
                            </span>
                          )} */}
                        </div>
                      </TableCell>

                      {/* <TableCell
                        className={`text-center tabular-nums text-xs text-indigo-600 font-bold border-r border-slate-200 py-1.5 ${cellDisabledClass}`}
                      >
                        {row.persentase &&
                        columnLabel &&
                        row.persentase[
                          columnLabel.toLowerCase() as keyof typeof row.persentase
                        ] !== undefined
                          ? `${(row.persentase[columnLabel.toLowerCase() as keyof typeof row.persentase] * 100).toFixed(0)}%`
                          : "-"}
                      </TableCell> */}

                      <TableCell
                        className={`text-center tabular-nums text-xs text-slate-600 font-bold border-r border-slate-200 py-1.5 ${cellDisabledClass}`}
                      >
                        {showNilai
                          ? akumulasiMatched
                            ? row.iku === 7
                              ? formatPoint(akumulasiMatched.max_point)
                              : formatPoint(akumulasiMatched.max_point_full)
                            : "0.00"
                          : "***"}
                      </TableCell>
                      <TableCell
                        className={`text-center tabular-nums text-xs text-emerald-600 font-bold border-r border-slate-200 py-1.5 ${cellDisabledClass}`}
                      >
                        {showNilai
                          ? akumulasiMatched
                            ? formatPoint(akumulasiMatched.final_point)
                            : "0.00"
                          : "***"}
                      </TableCell>
                      <TableCell className="text-center py-1.5">
                        {matchedIkuData?.entries?.length > 0 ||
                        matchedIkuData?.iku1_entries?.length > 0 ||
                        matchedIkuData?.iku2_entries?.length > 0 ? (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[10px] font-semibold text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                {matchedIkuData?.iku1_entries?.length > 0 ||
                                matchedIkuData?.iku2_entries?.length > 0
                                  ? "Lihat Data"
                                  : "Lihat Karya"}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
                              <DialogHeader className="px-6 py-4 border-b border-slate-100 shrink-0">
                                <DialogTitle className="flex flex-wrap items-center justify-between gap-4">
                                  <span className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                    Rincian Aktivitas{" "}
                                    <span className="text-indigo-600">
                                      {rowIkuText}
                                    </span>
                                  </span>
                                  <div className="bg-emerald-50 text-emerald-700 px-4 py-1.5 rounded-full text-xs border border-emerald-100 shadow-sm flex items-center gap-2">
                                    <span className="text-emerald-600/80 font-medium">
                                      Total Point Diperoleh:
                                    </span>
                                    <span className="text-sm font-bold tabular-nums">
                                      {showNilai
                                        ? formatPoint(
                                            akumulasiMatched?.final_point ??
                                              matchedIkuData?.real_point,
                                          )
                                        : "***"}
                                    </span>
                                  </div>
                                </DialogTitle>
                              </DialogHeader>
                              <div className="p-6 overflow-y-auto bg-slate-50/50">
                                <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-sm">
                                  {matchedIkuData?.iku1_entries?.length > 0 ? (
                                    <Table>
                                      <TableHeader className="bg-slate-50/80 border-b border-slate-200/80 sticky top-0 z-10 backdrop-blur-md">
                                        <TableRow className="hover:bg-transparent">
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            Prodi/Jurusan Homebase
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            Jenjang
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Total Mahasiswa
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Lulus Tepat Waktu
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            AEE Realisasi
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            AEE Ideal
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Pencapaian
                                          </TableHead>

                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">
                                            Point
                                          </TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {matchedIkuData.iku1_entries.map(
                                          (entry: any, eIdx: number) => (
                                            <TableRow
                                              key={eIdx}
                                              className="hover:bg-slate-50/50 transition-colors"
                                            >
                                              <TableCell className="text-xs font-medium text-slate-700 w-56 whitespace-normal leading-tight">
                                                {unitHomebase?.prodi ||
                                                  unitHomebase?.jurusan ||
                                                  "-"}
                                              </TableCell>
                                              <TableCell className="text-xs font-medium text-slate-500">
                                                {entry.jenjang}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.total_lulusan}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.lulus_tepat_waktu}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.aee_realisasi}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.aee_ideal}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.tingkat_pencapaian}
                                              </TableCell>
                                              <TableCell className="text-xs font-bold text-emerald-600 text-right tabular-nums">
                                                {showNilai
                                                  ? formatPoint(entry.point)
                                                  : "***"}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  ) : matchedIkuData?.iku2_entries?.length >
                                    0 ? (
                                    <Table>
                                      <TableHeader className="bg-slate-50/80 border-b border-slate-200/80 sticky top-0 z-10 backdrop-blur-md">
                                        <TableRow className="hover:bg-transparent">
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            Kode Unit
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                            Nama Unit
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Total Lulusan
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Total Tracer
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Target
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Bobot
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Score
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">
                                            Persentase
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">
                                            Point
                                          </TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {matchedIkuData.iku2_entries.map(
                                          (entry: any, eIdx: number) => (
                                            <TableRow
                                              key={eIdx}
                                              className="hover:bg-slate-50/50 transition-colors"
                                            >
                                              <TableCell className="text-xs font-medium text-slate-500">
                                                {entry.kode_unit}
                                              </TableCell>
                                              <TableCell className="text-xs font-medium text-slate-700">
                                                {entry.nama_unit}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.total_lulusan}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.total_tracer}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.minimum_target}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.total_bobot}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.score}
                                              </TableCell>
                                              <TableCell className="text-xs text-center font-medium text-slate-700">
                                                {entry.persentase}
                                              </TableCell>
                                              <TableCell className="text-xs font-bold text-emerald-600 text-right tabular-nums">
                                                {showNilai
                                                  ? formatPoint(entry.point)
                                                  : "***"}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  ) : (
                                    <Table>
                                      <TableHeader className="bg-slate-50/80 border-b border-slate-200/80 sticky top-0 z-10 backdrop-blur-md">
                                        <TableRow className="hover:bg-transparent">
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider w-20">
                                            Kode
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider max-w-75">
                                            Judul Aktivitas
                                          </TableHead>
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center w-24">
                                            Posisi
                                          </TableHead>
                                          {row.iku !== 8 && (
                                            <>
                                              <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center w-20">
                                                Anggota
                                              </TableHead>
                                              <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center w-24">
                                                Akreditasi
                                              </TableHead>
                                              <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center w-28">
                                                Indexer
                                              </TableHead>
                                            </>
                                          )}
                                          {row.iku === 6 && (
                                            <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center w-24">
                                              Top Tier
                                            </TableHead>
                                          )}
                                          <TableHead className="text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-24">
                                            Point
                                          </TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {matchedIkuData.entries?.map(
                                          (entry: any, eIdx: number) => (
                                            <TableRow
                                              key={eIdx}
                                              className="hover:bg-slate-50/50 transition-colors"
                                            >
                                              <TableCell className="text-xs font-medium text-slate-500">
                                                {entry.ren_code}
                                              </TableCell>
                                              <TableCell className="text-xs font-medium text-slate-700 leading-snug max-w-75 whitespace-normal wrap-break-word">
                                                {entry.judul}
                                              </TableCell>
                                              <TableCell className="text-xs text-center">
                                                {row.iku === 8 ? (
                                                  <Badge
                                                    variant="outline"
                                                    className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider shadow-none border-0 px-2.5 py-0.5"
                                                  >
                                                    Anggota/Tim
                                                  </Badge>
                                                ) : (
                                                  <div className="flex flex-col items-center gap-1">
                                                    <Badge
                                                      variant="outline"
                                                      className={`text-[10px] uppercase tracking-wider shadow-none border-0 px-2.5 py-0.5 ${
                                                        entry.is_ketua
                                                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                                                          : "bg-slate-100 text-slate-600"
                                                      }`}
                                                    >
                                                      {entry.is_ketua
                                                        ? "Ketua"
                                                        : "Anggota"}
                                                    </Badge>
                                                    {entry.is_koresponden && (
                                                      <Badge
                                                        variant="outline"
                                                        className="bg-purple-50 text-purple-700 font-semibold text-[10px] uppercase tracking-wider shadow-none border-0 px-2.5 py-0.5"
                                                      >
                                                        Koresponden
                                                      </Badge>
                                                    )}
                                                  </div>
                                                )}
                                              </TableCell>
                                              {row.iku !== 8 && (
                                                <>
                                                  <TableCell className="text-xs text-center text-slate-500">
                                                    {entry.jumlah_anggota ||
                                                      "-"}
                                                  </TableCell>
                                                  <TableCell className="text-xs text-center text-slate-500">
                                                    {entry.akreditasi || "-"}
                                                  </TableCell>
                                                  <TableCell className="text-xs text-center text-slate-500">
                                                    {entry.indexer || "-"}
                                                  </TableCell>
                                                </>
                                              )}
                                              {row.iku === 6 && (
                                                <TableCell className="text-xs text-center text-slate-500">
                                                  {entry.top_tier == 1
                                                    ? "Ya"
                                                    : "Tidak"}
                                                </TableCell>
                                              )}
                                              <TableCell className="text-xs font-bold text-emerald-600 text-right tabular-nums">
                                                {showNilai
                                                  ? formatPoint(entry.point)
                                                  : "***"}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  )}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        ) : (
                          <div className="text-[10px] text-slate-400 font-medium">
                            -
                          </div>
                        )}
                      </TableCell>
                      {onClaimIku !== undefined && (
                        <TableCell
                          className={`text-center py-1.5 ${cellDisabledClass}`}
                        >
                          {isActive &&
                            onClaimIku &&
                            (isClaimed ? (
                              <Button
                                size="sm"
                                onClick={() => onClaimIku(null)}
                                disabled={isClaiming}
                                variant="destructive"
                                className="h-6 px-3 py-0 rounded-md font-medium text-[10px]"
                              >
                                {isClaiming ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : null}
                                Batalkan Claim
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => {
                                  onClaimIku(`IKU ${row.iku}`);
                                }}
                                disabled={
                                  isClaiming
                                  // || (row.iku === 7 &&
                                  //   activeProduct?.details_parsed?.is_ketua ===
                                  //     false)
                                }
                                className="h-6 px-3 py-0 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium text-[10px] disabled:bg-slate-300 disabled:text-slate-500"
                              >
                                {isClaiming ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : null}
                                {/* {row.iku === 7 &&
                                activeProduct?.details_parsed?.is_ketua ===
                                  false
                                  ? "Bukan Ketua"
                                  : "Pilih"} */}
                                Pilih
                              </Button>
                            ))}
                        </TableCell>
                      )}
                    </TableRow>
                  </Fragment>
                );
              });
            })()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
